"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import type { CampaignPageSection } from "@growwithai/shared";
import { useTenant } from "@/components/tenant/TenantProvider";
import { isSafeHref } from "../types";
import { sectionBgStyle } from "../section-style";
type FooterProps = Extract<CampaignPageSection, {
  type: "footer";
}>;

// Ikon brand tidak lagi tersedia di lucide-react → SVG inline sederhana.
function InstagramIcon(t0) {
  const $ = _c(5);
  const {
    className
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />;
    t2 = <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />;
    t3 = <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== className) {
    t4 = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden={true}>{t1}{t2}{t3}</svg>;
    $[3] = className;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function TiktokIcon(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden={true}>{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function YoutubeIcon(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />;
    t2 = <path d="m10 15 5-3-5-3z" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== className) {
    t3 = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden={true}>{t1}{t2}</svg>;
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
const DEFAULT_LINKS: {
  label: string;
  href: string;
}[] = [{
  label: "Kontak",
  href: "/kontak"
}, {
  label: "Syarat & Ketentuan",
  href: "/terms"
}, {
  label: "Kebijakan Privasi",
  href: "/privacy"
}];

/** Footer landing: deskripsi, tautan tenant (safeHref), sosial, © brand. */
export default function FooterSection(t0) {
  const $ = _c(43);
  const {
    section
  } = t0;
  const branding = useTenant();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Date().getFullYear();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const year = t1;
  let socials;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[1] !== branding.brand_name || $[2] !== section) {
    const tenantLinks = (section?.links ?? []).filter(_temp);
    socials = section?.socials;
    t7 = "bg-warm-black text-white";
    const t9 = section?.custom_style;
    if ($[11] !== t9) {
      t8 = sectionBgStyle(t9);
      $[11] = t9;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    t6 = "mx-auto max-w-6xl px-5 py-12";
    t4 = "flex flex-col gap-8 md:flex-row md:items-start md:justify-between";
    let t10;
    if ($[13] !== branding.brand_name) {
      t10 = <p className="text-lg font-bold">{branding.brand_name}</p>;
      $[13] = branding.brand_name;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    let t11;
    if ($[15] !== section) {
      t11 = section?.description && <p className="mt-2 text-sm leading-relaxed text-white/60">{section.description}</p>;
      $[15] = section;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    if ($[17] !== t10 || $[18] !== t11) {
      t5 = <div className="max-w-sm">{t10}{t11}</div>;
      $[17] = t10;
      $[18] = t11;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    t2 = "flex flex-col gap-6 sm:flex-row sm:gap-12";
    t3 = tenantLinks.length > 0 && <nav aria-label="Tautan" className="flex flex-col gap-2">{tenantLinks.map(_temp2)}</nav>;
    $[1] = branding.brand_name;
    $[2] = section;
    $[3] = socials;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    socials = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <nav aria-label="Halaman" className="flex flex-col gap-2">{DEFAULT_LINKS.map(_temp3)}</nav>;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t2 || $[22] !== t3) {
    t10 = <div className={t2}>{t3}{t9}</div>;
    $[21] = t2;
    $[22] = t3;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t4 || $[26] !== t5) {
    t11 = <div className={t4}>{t5}{t10}</div>;
    $[24] = t10;
    $[25] = t4;
    $[26] = t5;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== branding.brand_name) {
    t12 = <p className="text-xs text-white/50">© {year} {branding.brand_name}. Hak cipta dilindungi.</p>;
    $[28] = branding.brand_name;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== socials) {
    t13 = socials && <div className="flex items-center gap-3">{socials.instagram && <a href={socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="-m-[13px] inline-block p-[13px] text-white/60 transition-colors hover:text-white"><InstagramIcon className="h-[18px] w-[18px]" /></a>}{socials.tiktok && <a href={socials.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="-m-[13px] inline-block p-[13px] text-white/60 transition-colors hover:text-white"><TiktokIcon className="h-[18px] w-[18px]" /></a>}{socials.youtube && <a href={socials.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="-m-[13px] inline-block p-[13px] text-white/60 transition-colors hover:text-white"><YoutubeIcon className="h-[18px] w-[18px]" /></a>}</div>;
    $[30] = socials;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t12 || $[33] !== t13) {
    t14 = <div className="mt-10 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center">{t12}{t13}</div>;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t11 || $[36] !== t14 || $[37] !== t6) {
    t15 = <div className={t6}>{t11}{t14}</div>;
    $[35] = t11;
    $[36] = t14;
    $[37] = t6;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t15 || $[40] !== t7 || $[41] !== t8) {
    t16 = <footer className={t7} style={t8}>{t15}</footer>;
    $[39] = t15;
    $[40] = t7;
    $[41] = t8;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  return t16;
}
function _temp3(l_1) {
  return <Link key={l_1.href} href={l_1.href} className="text-sm text-white/70 transition-colors hover:text-white">{l_1.label}</Link>;
}
function _temp2(l_0, i) {
  return l_0.href.startsWith("/") ? <Link key={i} href={l_0.href} className="text-sm text-white/70 transition-colors hover:text-white">{l_0.label}</Link> : <a key={i} href={l_0.href} target="_blank" rel="noopener noreferrer" className="text-sm text-white/70 transition-colors hover:text-white">{l_0.label}</a>;
}
function _temp(l) {
  return isSafeHref(l.href);
}